import { useCallback, useState } from "react";
import { useProgressApiClient } from "@api/progress-api/use-progress-api-client";
import { useProjectApiClient } from "@api/project-api/use-project-api-client";
import { useRegistrationApiClient } from "@api/registration-api/use-registration-api-client";
import { FaroButton } from "@components/common/faro-button";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { BaseProjectIdProps } from "@custom-types/sdb-company-types";
import { CaptureTreeRevision } from "@custom-types/capture-tree/capture-tree-types";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { fetchCaptureTreeData, fetchRevisionsAndDraftEntities } from "@hooks/data-management/use-data-management";
import { useToast } from "@hooks/use-toast";
import { ButtonProps } from "@mui/material";
import { areProjectPointCloudAndMapOutdated } from "@pages/data-management/publish-utils";
import { DELAY_FOR_REGISTRATION_TASK } from "@pages/data-management/register-data-utils";
import { captureTreeForOpenDraftRevisionSelector, openDraftRevisionSelector } from "@store/capture-tree/capture-tree-selectors";
import { useAppSelector, useAppDispatch } from "@store/store-helper";
import { DataManagementEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { changeSummaryForTracking, summarizeRevisionChanges, wasDraftUpdated } from "@utils/capture-tree/capture-tree-changes";
import { delay } from "@utils/time-utils";

interface Props extends BaseProjectIdProps {
  isLoading: boolean;
  isDisabled: boolean;
  /** Flag if we're coming from PublishPanel, and need to rebuild the PPC and map. */
  isFromPanel: boolean;
  /** Tooltip for the button. */
  tooltip: string;
  /** Data test ID for testing */
  dataTestId?: string;
  /**
   * Optional callback when the button is clicked, called before publishing the draft.
   * Allows main onClick function to be skipped if by returning `false`. E.g. on error, or failed validation checks.
   */
  onClickBefore?: () => Promise<boolean>;
  /** Optional callback when the button is clicked, called after publishing the draft. */
  onClickAfter?: () => void;
  /** Optional sx properties to override the button style. */
  sx?: ButtonProps["sx"];
}

export function PublishButton({
  projectId,
  isLoading,
  isDisabled,
  isFromPanel,
  tooltip,
  dataTestId,
  onClickBefore,
  onClickAfter,
  sx = {},
}: Props): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const projectApiClient = useProjectApiClient({ projectId });
  const progressApiClient = useProgressApiClient({ projectId });
  const registrationApiClient = useRegistrationApiClient({ projectId });
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const { handleErrorWithToast } = useErrorContext();
  const openDraftRevisionRender = useAppSelector(openDraftRevisionSelector);
  const openDraftEntitiesRender = useAppSelector(captureTreeForOpenDraftRevisionSelector);

  const [isLoadingInternal, setIsLoadingInternal] = useState<boolean>(false);

  /**
   * Ask for confirmation, then publish the draft revision.
   * If the project was updated in the meantime, show a warning and early out.
   */
  const publishDraft = useCallback(async () => {
    let hasOperationFailed = false;
    let openDraftEntities, openDraftRevision, shouldRunRegistrationWorker;

    try {
      let revisions: CaptureTreeRevision[] | undefined;

      try {
        // Fetch again, to ensure that there is something to publish.
        const result = await fetchRevisionsAndDraftEntities(dispatch, projectApiClient);
        openDraftEntities = result.openDraftEntities;
        openDraftRevision = result.openDraftRevision;
        revisions = result.revisions;

        // Verify that the state shown on the page still matches the latest state.
        if (!openDraftRevisionRender && !openDraftRevision) {
          // No error, continue below: Re-publishing without changes.
        } else if (
          !openDraftRevisionRender || !openDraftRevision ||
          wasDraftUpdated(openDraftRevisionRender, openDraftEntitiesRender, openDraftRevision, openDraftEntities)
        ) {
          hasOperationFailed = true;
          showToast({
            message: "The project has been updated by another user or application. Please review the changes and try again.",
            type: "warning",
          });
          return;
        }
      } catch (error) {
        hasOperationFailed = true;
        handleErrorWithToast({
          id: `publishDraftRevision-${new Date().toString()}`,
          error,
          title: "Failed to fetch latest project state before publishing. Please reload the page to try again.",
        });
        return;
      }

      try {
        // - If we're coming from PublishPanel, we've already determined that we need to update the map or project point cloud,
        //   since we showed the publish options (at least with alpha flag).
        // - If there is no draft, everything is published, and we cannot call the publish endpoint directly.
        //   Also it's probably a re-publish with different options (e.g. reflection filter), so we want to update the map
        //   and/or project point cloud.
        // - Implementation detail: Updating the map and project point cloud is done by the registration worker.
        //   In the near future, there will be a dedicated worker for it.
        shouldRunRegistrationWorker =
          isFromPanel ||
          !openDraftRevision ||
          areProjectPointCloudAndMapOutdated(openDraftEntities, openDraftRevision, revisions);

        // The check `!openDraftRevision` is redundant, but needed for TypeScript (else branch).
        if (!openDraftRevision || shouldRunRegistrationWorker) {
          await registrationApiClient.startCaptureTreeRegistration({
            // eslint-disable-next-line @typescript-eslint/naming-convention -- defined by the API
            autoPublish: true,
            // Don't run another registration, but only update the map and project point cloud.
            preset: "merge-only",
          });

          // Make sure that the registration task already exists when we call fetchCaptureTreeData() below.
          await delay(DELAY_FOR_REGISTRATION_TASK);
        } else {
          await projectApiClient.publishDraftRevision(openDraftRevision.id);
        }
      } catch (error) {
        hasOperationFailed = true;
        handleErrorWithToast({
          id: `publishDraftRevision-${new Date().toString()}`,
          error,
          title: "Failed to publish the project. Please reload the page and try again.",
        });
      }
    } finally {
      trackEvent({
        name: DataManagementEvents.publish,
        props: {
          draftRevision: openDraftRevision?.id ?? false,
          changeSummary: openDraftEntities ? changeSummaryForTracking(summarizeRevisionChanges(openDraftEntities)) : {},
          shouldRunRegistrationWorker: shouldRunRegistrationWorker ?? false,
          didOperationFail: hasOperationFailed,
        },
      });

      // Fetch the new state immediately instead of waiting until the next usePolling() happens.
      try {
        await fetchCaptureTreeData(dispatch, projectApiClient, progressApiClient);
      } catch { /* Not essential. The UI will update a bit later. */ }
    }
  }, [
    dispatch, handleErrorWithToast, isFromPanel, openDraftEntitiesRender, openDraftRevisionRender, progressApiClient,
    projectApiClient, registrationApiClient, showToast, trackEvent,
  ]);

  /**
   * Calls the provided onClickBefore handler, then publishes the draft, and then calls the provided onClickAfter
   * handler.
   */
  const onClick = useCallback(async() => {
    try {
      setIsLoadingInternal(true);

      if (onClickBefore) {
        // Don't publish if onClickBefore() fails or resolves to false.
        const isSuccessful = await onClickBefore();
        if (!isSuccessful) {
          return;
        }
      }
      await publishDraft();
      if (onClickAfter) {
        onClickAfter();
      }
    } finally {
      setIsLoadingInternal(false);
    }
  }, [onClickAfter, onClickBefore, publishDraft]);

  return (
    <SphereTooltip
      title={tooltip}
      dataTestId="sa-publish-tooltip"
      enterDelay={isDisabled ? 0 : undefined}
    >
      <FaroButton
        dataTestId={dataTestId}
        isDisabled={isDisabled}
        isLoading={isLoading || isLoadingInternal}
        sx={sx}
        onClick={onClick}
      >
        Publish
      </FaroButton>
    </SphereTooltip>
  );
}
